/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ItemInt } from '../config/api';

interface BrandSwitcherProps {
  brands: ItemInt[];
  selectedBrand?: number;
  onBrandSelect: (brandId: number) => void;
  recentBrands?: number[];
}

/**
 * Elegant brand switcher with search, recent brands, and visual hierarchy
 */
const BrandSwitcher: React.FC<BrandSwitcherProps> = ({
  brands,
  selectedBrand,
  onBrandSelect,
  recentBrands = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when opening
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter brands based on search
  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get recent brand objects
  const recentBrandObjects = recentBrands
    .map(id => brands.find(b => b.id === id))
    .filter(Boolean) as ItemInt[];

  // Group brands by category
  const brandsByCategory = filteredBrands.reduce((acc, brand) => {
    const category = /* brand.category || */ 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(brand);
    return acc;
  }, {} as Record<string, ItemInt[]>);

  const getBrandInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const getBrandColor = (brandId: number) => {
    // Generate consistent color based on brand ID
    const colors = [
      'from-blue-500 to-indigo-600',
      'from-green-500 to-emerald-600',
      'from-purple-500 to-pink-600',
      'from-yellow-500 to-orange-600',
      'from-red-500 to-rose-600',
      'from-cyan-500 to-teal-600'
    ];
    const index = brandId % colors.length;
    return colors[index];
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Current Brand Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1 border border-border/50 rounded-full hover:border-primary/40 hover:bg-primary/5 transition-all"
      >
        {selectedBrand ? (
          <span className="text-foreground text-sm">{brands.find(b => b.id === selectedBrand)?.name ?? selectedBrand} selected</span>
        ) : (
          <span className="text-text-muted text-sm">Select</span>
        )}
        <i className={`fas fa-chevron-down text-text-muted text-[10px] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-card backdrop-blur-sm border border-border rounded-xl shadow-2xl overflow-hidden z-dropdown">
          {/* Search Bar */}
          <div className="p-3 border-b border-[var(--border)]">
            <div className="relative">
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted text-sm" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search brands..."
                className="w-full pl-9 pr-3 py-2 bg-background-tertiary border border-[var(--border)] rounded-lg text-foreground placeholder-text-muted text-sm focus:outline-none focus:border-primary/50"
              />
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="max-h-96 overflow-y-auto">
            {/* Recent Brands */}
            {!searchQuery && recentBrandObjects.length > 0 && (
              <div className="p-3 border-b border-[var(--border)]">
                <p className="text-xs text-text-muted uppercase font-semibold mb-2">Recent</p>
                {recentBrandObjects.map(brand => (
                  <button
                    key={brand?.id}
                    onClick={() => onBrandSelect(brand.id)}
                    className={`w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-primary/10 transition-colors ${
                      selectedBrand === brand?.id ? 'bg-primary/20' : ''
                    }`}
                  >
                    <div className={`w-6 h-6 rounded bg-gradient-to-br ${getBrandColor(brand?.id)} flex items-center justify-center text-white text-[10px] font-bold`}>
                      {getBrandInitials(brand?.name)}
                    </div>
                    <span className="text-foreground text-sm">{brand.name}</span>
                  </button>
                ))}
              </div>
            )}

            {/* All Brands by Category */}
            <div className="p-3">
              {Object.entries(brandsByCategory).map(([category, categoryBrands]) => (
                <div key={category} className="mb-4">
                  <p className="text-xs text-text-muted uppercase font-semibold mb-2">{category}</p>
                  <div className="space-y-1">
                    {categoryBrands.map(brand => (
                      <button
                        key={brand.id}
                        onClick={() => onBrandSelect(brand.id)}
                        className={`w-full flex items-center space-x-3 px-2 py-2 rounded-lg hover:bg-primary/10 transition-colors ${
                          selectedBrand === brand.id ? 'bg-primary/20' : ''
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${getBrandColor(brand.id)} flex items-center justify-center text-white text-xs font-bold shadow`}>
                          {getBrandInitials(brand.name)}
                        </div>
                        <div className="text-left flex-1">
                          <p className="text-foreground text-sm">{brand.name}</p>
                        </div>
                        {selectedBrand === brand.id && (
                          <i className="fas fa-check text-primary text-xs"></i>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* No results */}
            {filteredBrands.length === 0 && (
              <div className="p-6 text-center">
                <p className="text-text-muted text-sm">No brands found matching "{searchQuery}"</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandSwitcher;
